import { Eye, FileCode01 } from "@untitledui/icons"
import { useEffect, useState } from "react"

import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"

/**
 * A sidebar dev-tools toggle backed by a localStorage flag + a CustomEvent, so
 * interested components (SimulationSurface's debug panel, AssessorView) can
 * re-render when it flips. Shared by the chat and replay dev-tools groups so the
 * behaviour is identical everywhere. Off by default.
 */

export interface ToggleSpec {
  storageKey: string
  changeEvent: string
  label: string
  icon: typeof Eye
}

export const DEBUG_EVENTS_TOGGLE: ToggleSpec = {
  storageKey: "npc_debug",
  changeEvent: "npc-debug-changed",
  label: "Debug Events",
  icon: FileCode01,
}

export const ASSESSOR_VIEW_TOGGLE: ToggleSpec = {
  storageKey: "assessor_view",
  changeEvent: "assessor-view-changed",
  label: "Assessor View",
  icon: Eye,
}

export function DevToggle({ spec }: { spec: ToggleSpec }) {
  const [enabled, setEnabled] = useState(false)
  const Icon = spec.icon

  useEffect(() => {
    if (typeof window === "undefined") return
    const v = localStorage.getItem(spec.storageKey)
    setEnabled(v === "1" || v === "true")
  }, [spec.storageKey])

  const toggle = () => {
    if (typeof window === "undefined") return
    const next = !enabled
    setEnabled(next)
    if (next) localStorage.setItem(spec.storageKey, "1")
    else localStorage.removeItem(spec.storageKey)
    try {
      window.dispatchEvent(
        new CustomEvent(spec.changeEvent, { detail: { value: next } }),
      )
    } catch {
      /* ignored: best-effort notification, safe to skip if CustomEvent is unavailable */
    }
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={toggle}
        isActive={enabled}
        className="cursor-pointer"
      >
        <Icon />
        <span>{spec.label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
